import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import API from "../Network/API";
import "./SinglePage.css";

// Footer menu location -> its group name (same labels as the footer columns).
const SECTION_LABELS = {
    about: "footer.about_us",
    student: "footer.for_students",
    teacher: "footer.for_tutors",
    career: "footer.xller_career",
    support: "footer.support",
};

// CMS pages linked from the footer (About us, Test your English, Careers, ...).
export default function SinglePage() {
    const { t } = useTranslation();
    const { slug } = useParams();
    const location = useLocation();
    const prefix = location.pathname.startsWith("/app") ? "/app" : "";
    const menus = useSelector((state) => state.menuReducer);

    const [page, setPage] = useState(null);
    const [state, setState] = useState("loading");
    const [imageOk, setImageOk] = useState(true);

    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        setState("loading");
        setImageOk(true);
        window.scrollTo(0, 0);
        API.get(`/pages/${slug}`)
            .then((response) => {
                const data = response.data?.data;
                setPage(data || null);
                setState(data ? "ready" : "missing");
            })
            // only a real 404 means the page doesn't exist; rate limits or network errors can be retried
            .catch((err) => setState(err?.response?.status === 404 ? "missing" : "error"));
    }, [slug, attempt]);

    // Tab title + meta description for sharing and search.
    useEffect(() => {
        if (!page) return undefined;
        const previous = document.title;
        document.title = `${page.title} | Xller`;
        let meta = document.querySelector('meta[name="description"]');
        const previousDescription = meta?.getAttribute("content");
        if (page.meta_description) {
            if (!meta) {
                meta = document.createElement("meta");
                meta.setAttribute("name", "description");
                document.head.appendChild(meta);
            }
            meta.setAttribute("content", page.meta_description);
        }
        return () => {
            document.title = previous;
            if (meta && previousDescription != null) meta.setAttribute("content", previousDescription);
        };
    }, [page]);

    const items = Array.isArray(menus?.data) ? menus.data : [];
    const current = items.find((m) => m.type === "page" && String(m.url).toLowerCase() === String(slug).toLowerCase());
    const sectionKey = current ? SECTION_LABELS[current.location] : null;
    const related = current
        ? items.filter((m) => m.type === "page" && m.location === current.location && m.url !== current.url)
        : [];

    if (state === "error") {
        return (
            <div className="sp-page">
                <div className="container">
                    <div className="sp-missing">
                        <i className="fa-light fa-wifi-exclamation" />
                        <h1>{t("singlePage.error_title")}</h1>
                        <p>{t("singlePage.error_text")}</p>
                        <button type="button" className="sp-btn sp-btn-primary" onClick={() => setAttempt((n) => n + 1)}>{t("singlePage.retry")}</button>
                    </div>
                </div>
            </div>
        );
    }

    if (state === "missing") {
        return (
            <div className="sp-page">
                <div className="container">
                    <div className="sp-missing">
                        <i className="fa-light fa-file-circle-question" />
                        <h1>{t("singlePage.not_found_title")}</h1>
                        <p>{t("singlePage.not_found_text")}</p>
                        <Link to={`${prefix}/`} className="sp-btn sp-btn-primary">{t("singlePage.back_home")}</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="sp-page">
            <header className="sp-hero">
                <div className="container">
                    <nav className="sp-crumbs" aria-label={t("singlePage.breadcrumb")}>
                        <Link to={`${prefix}/`}>{t("singlePage.home")}</Link>
                        {sectionKey && <><i className="fa-light fa-chevron-right" /><span>{t(sectionKey)}</span></>}
                    </nav>
                    {state === "loading"
                        ? <><span className="sp-sk sp-sk-title" /><span className="sp-sk sp-sk-line" /></>
                        : <>
                            <h1 className="sp-title">{page?.title}</h1>
                            {page?.meta_description && <p className="sp-lead">{page.meta_description}</p>}
                        </>
                    }
                </div>
            </header>

            <div className="container">
                <div className="sp-layout">
                    <article className="sp-article">
                        {state === "loading" ?
                            <>
                                <span className="sp-sk sp-sk-image" />
                                {[90, 100, 95, 70].map((w, i) => <span key={i} className="sp-sk sp-sk-line" style={{ width: `${w}%` }} />)}
                            </>
                            :
                            <>
                                {page?.image && imageOk &&
                                    <figure className="sp-figure">
                                        <img src={page.image} alt={page.title} onError={() => setImageOk(false)} />
                                    </figure>
                                }
                                <div className="sp-content" dangerouslySetInnerHTML={{ __html: page?.details || "" }} />
                            </>
                        }
                    </article>

                    <aside className="sp-aside">
                        {related.length > 0 &&
                            <div className="sp-card">
                                <h2 className="sp-card-title">{sectionKey ? t(sectionKey) : t("singlePage.more_pages")}</h2>
                                <ul className="sp-links">
                                    {related.map((m) => (
                                        <li key={m.id}>
                                            <Link to={`${prefix}/page/${m.url}`}>
                                                <span>{m.title}</span>
                                                <i className="fa-light fa-arrow-right" />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        }
                        <div className="sp-card sp-cta">
                            <i className="fa-light fa-graduation-cap sp-cta-icon" />
                            <h2 className="sp-card-title">{t("singlePage.cta_title")}</h2>
                            <p>{t("singlePage.cta_text")}</p>
                            <Link to={`${prefix}/all-courses`} className="sp-btn sp-btn-primary">{t("singlePage.browse_courses")}</Link>
                            <Link to={`${prefix}/teachers`} className="sp-btn sp-btn-ghost">{t("singlePage.find_tutor")}</Link>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}
